import moment from "moment";
import memoize from "lodash/memoize";
import _startsWith from "lodash/startsWith";
import { Map, List, Set } from "immutable";

import {
  GROUP_TYPES,
  SHIFT_TYPES,
  VIEW_TYPES,
  FIELD_STATUS,
  FACILITY_TYPE,
  FACILITY_STATUS,
  SEARCH_OBJECT_TYPES,
  BOOKING_SYSTEM_TYPES,
  WORK_SCHEDULE_STATUS,
  CALENDAR_DATE_RANGE_TYPE,
  WORK_SCHEDULE_OVERRIDE_REASONS,
  CARD_CONTAINER_TYPE,
  EMPLOYEE_TYPES,
  REQUIREMENT_TYPES,
  CRITICALITY,
} from "features/planner/planner.constants";

import Mapper from "infrastructure/mapper";
import { EMPTY_MAP, EMPTY_LIST } from "app/app.constants";
import { dateFromDateHourMinute, createGuid } from "utils/app.util";
import theme from "layout/themes";

const START_END_DATE_FORMAT = "YYYY-MM-DD";

export const facilityStatusToString = memoize(
  facilityStatus =>
    ({
      [FACILITY_STATUS.UNKNOWN]: "Unknown",
      [FACILITY_STATUS.ABANDONED]: "Abandoned",
      [FACILITY_STATUS.DECOMMISSIONED]: "Decommisioned",
      [FACILITY_STATUS.FABRICATION]: "Fabrication",
      [FACILITY_STATUS.FUTURE]: "Future",
      [FACILITY_STATUS.INSTALLATION]: "Installation",
      [FACILITY_STATUS.LAID_UP]: "Laid Up",
      [FACILITY_STATUS.IN_SERVICE]: "In Service",
      [FACILITY_STATUS.REMOVED]: "Removed",
    }[facilityStatus]),
);

export const fieldStatusToString = memoize(
  fieldStatus =>
    ({
      [FIELD_STATUS.UNKNOWN]: "Unknown",
      [FIELD_STATUS.PRODUCING]: "Producing",
      [FIELD_STATUS.PRODUCTION_UNLIKELY]: "Production unlikely",
      [FIELD_STATUS.PRODUCTION_LIKELY]: "Production likely",
      [FIELD_STATUS.PRODUCTION_APPROVED]: "Production approved",
      [FIELD_STATUS.PRODUCTION_IN_CLARIFICATION_PHASE]:
        "Production in clarification phase",
      [FIELD_STATUS.PRODUCTION_NOT_EVALUATED]: "Production not evaluated",
      [FIELD_STATUS.PRODUCTION_DECIDED]: "Production decided",
      [FIELD_STATUS.INCLUDED_IN_OTHER_DISCOVERY]: "Included in other discovery",
      [FIELD_STATUS.SHUT_DOWN]: "Shut down",
    }[fieldStatus]),
);

export const facilityTypeToString = memoize(
  facilityType =>
    ({
      [FACILITY_TYPE.FIXED]: "Fixed",
      [FACILITY_TYPE.MOBILE]: "Mobile",
    }[facilityType]),
);

export const cardContainerTypeToString = memoize(
  cardContainerType =>
    ({
      [CARD_CONTAINER_TYPE.ONSHORE]: "Onshore",
      [CARD_CONTAINER_TYPE.ALLOCATED_OFFSHORE]: "Allocated",
      [CARD_CONTAINER_TYPE.DEPLOYED_OFFSHORE]: "Offshore",
    }[cardContainerType]),
);

export const bookingSystemTypeToString = memoize(
  bookingSystemType =>
    ({
      [BOOKING_SYSTEM_TYPES.DA_WINCHI]: "DaWinci",
      [BOOKING_SYSTEM_TYPES.TICKET]: "Ticket",
      [BOOKING_SYSTEM_TYPES.VANTAGE]: "Vantage",
    }[bookingSystemType]),
);

export const groupTypeToString = memoize(
  groupType =>
    ({
      [GROUP_TYPES.LOCATION_GROUP]: "Location Group",
      [GROUP_TYPES.PLANNING_GROUP]: "Planning Group",
    }[groupType]),
);

export const shiftTypeToString = memoize(
  shiftType =>
    ({
      [SHIFT_TYPES.DAY]: "Day",
      [SHIFT_TYPES.NIGHT]: "Night",
      [SHIFT_TYPES.MID]: "Mid",
    }[shiftType]),
);

export const searchObjectTypeToString = memoize(
  searchObjectType =>
    ({
      [SEARCH_OBJECT_TYPES.ALL]: "All",
      [SEARCH_OBJECT_TYPES.CARD]: "Cards",
      [SEARCH_OBJECT_TYPES.GROUP]: "Groups",
    }[searchObjectType]),
);

export const viewTypeToString = memoize(
  viewType =>
    ({
      [VIEW_TYPES.PRIVATE]: "Private",
      [VIEW_TYPES.PUBLIC]: "Public",
    }[viewType]),
);

export const employeeTypeToString = memoize(
  employeeType =>
    ({
      [EMPLOYEE_TYPES.EMPLOYEE]: "Employee",
      [EMPLOYEE_TYPES.SUB_CONTRACTOR]: "Sub Contractor",
    }[employeeType]),
);

export const ViewGroupMapper = new Mapper({
  initial: () =>
    Map({
      id: null,
      groupVisualState: 0,
    }),
  from: ({ viewGroupId, group, ...viewGroup }) =>
    Map({
      id: viewGroupId,
      type: group.type,
      ...viewGroup,
    }),
  to: ({ id, ...viewGroup }) => ({
    viewGroupId: id,
    ...viewGroup,
  }),
});

export const CardContainerMapper = new Mapper({
  initial: () =>
    Map({
      id: null,
    }),
  from: ({ cardContainerId, ...cardContainer }) =>
    Map({
      id: cardContainerId,
      ...cardContainer,
    }),
  to: ({ id }) => ({
    cardContainerId: id,
  }),
});

export const GroupMapper = new Mapper({
  initial: () => EMPTY_MAP,
  from: ({
    groupId,
    cardContainers,
    modified,
    facilityId,
    mobileFacilityId,
    ...group
  }) =>
    Map({
      id: groupId,
      cardContainersById: Map(
        List(cardContainers).map(cardContainer => [
          cardContainer.cardContainerId.toString(),
          CardContainerMapper.from(cardContainer),
        ]),
      ),
      modified: moment(modified),
      facilityId: facilityId || mobileFacilityId,
      facilityType: facilityId ? FACILITY_TYPE.FIXED : FACILITY_TYPE.MOBILE,
      ...group,
    }),
  to: ({ id, facilityId, facilityType, ...group }) => ({
    facilityId: facilityType === FACILITY_TYPE.FIXED ? facilityId : undefined,
    groupId: id,
    mobileFacilityId:
      facilityType === FACILITY_TYPE.MOBILE ? facilityId : undefined,
    ...group,
  }),
});

export const ViewMapper = new Mapper({
  initial: () =>
    Map({
      id: null,
      name: "",
      type: VIEW_TYPES.PRIVATE,
    }),
  from: ({ viewId, viewGroups, singleColumns, doubleColumns, ...view }) =>
    Map({
      id: viewId,
      columnCount: Map({
        [GROUP_TYPES.LOCATION_GROUP]: doubleColumns,
        [GROUP_TYPES.PLANNING_GROUP]: singleColumns,
      }),
      ...view,
    }),
  to: ({ id, viewGroups, columnCount, ...view }) => ({
    viewId: id,
    doubleColumns: columnCount
      ? columnCount[GROUP_TYPES.LOCATION_GROUP.toString()]
      : undefined,
    singleColumns: columnCount
      ? columnCount[GROUP_TYPES.PLANNING_GROUP.toString()]
      : undefined,
    viewGroups: viewGroups ? viewGroups.map(ViewGroupMapper.to) : undefined,
    ...view,
  }),
});

export const CardMapper = new Mapper({
  initial: () =>
    Map({
      employee: EmployeeMapper.initial(),
    }),
  from: ({ cardId, employee, currentWorkScheduleOverride, ...card }) =>
    Map({
      id: cardId,
      employee: employee ? EmployeeMapper.from(employee) : null,
      currentWorkScheduleOverride: currentWorkScheduleOverride
        ? WorkScheduleOverrideMapper.from(currentWorkScheduleOverride)
        : null,
      ...card,
    }),
  to: ({ id, ...card }) => ({
    employeeId: id,
    dummyCardId: id,
    ...card,
  }),
});

export const MarkerMapper = new Mapper({
  from: ({ markerId, modified, icon, ...marker }) =>
    Map({
      id: markerId,
      modified: moment(modified),
      icon: _startsWith(icon, "data:") ? icon : `data:${icon}`,
      files: Set([icon]),
      ...marker,
    }),
  to: ({ id, ...marker }) => ({
    markerId: id,
    ...marker,
  }),
});

export const EmployeeMapper = new Mapper({
  initial: () =>
    Map({
      id: null,
      lastName: "",
      firstName: "",
      projects: EMPTY_LIST,
    }),
  from: ({
    employeeId,
    airport,
    dateOfBirth,
    daysLeftOfDeployment,
    daysSinceDeploymentStart,
    deploymentShiftType,
    earliestNextDeployment,
    hiredFrom,
    hiredTo,
    markerId1,
    markerId2,
    markerId3,
    markerId4,
    nokAddress,
    nokCity,
    nokFirstName,
    nokLastName,
    nokPhone1,
    nokPhone2,
    nokType,
    nokZipCode,
    travelSysDaWinci,
    travelSysTicket,
    travelSysVantage,
    employeeType,
    projects,
    ...employee
  }) =>
    Map({
      id: employeeId,
      dateOfBirth: moment(dateOfBirth),
      hiredFrom: hiredFrom ? moment(hiredFrom) : null,
      hiredTo: hiredTo ? moment(hiredTo) : null,
      projects: List(projects).map(ProjectMapper.from),
      deployment: {
        daysLeft: daysLeftOfDeployment,
        daysSinceStart: daysSinceDeploymentStart,
        shiftType: deploymentShiftType,
        earliestNextDeployment: earliestNextDeployment
          ? moment(earliestNextDeployment)
          : null,
      },
      nextOfKin: {
        firstName: nokFirstName,
        lastName: nokLastName,
        displayName: `${nokFirstName || ""} ${nokLastName || ""}`,
        phone1: nokPhone1,
        phone2: nokPhone2,
        address: nokAddress,
        zipCode: nokZipCode,
        city: nokCity,
        type: nokType,
      },
      travel: {
        airport: airport,
        daWinci: travelSysDaWinci,
        ticket: travelSysTicket,
        vantage: travelSysVantage,
      },
      markers: [markerId1, markerId2, markerId3, markerId4].reduce(
        (sum, markerId, index) => sum.set((index + 1).toString(), markerId),
        Map(),
      ),
      type: employeeType,
      ...employee,
    }),
  to: ({ id, travel, markers, ...employee }) => ({
    employeeId: id,
    travelSysDaWinci: travel.daWinci,
    travelSysTicket: travel.ticket,
    travelSysVantage: travel.vantage,
    airport: travel.airport,
    ...Object.values(markers).reduce(
      (sum, curr, index) => ({
        ...sum,
        [`MarkerId${index + 1}`]: curr,
      }),
      {},
    ),
    ...employee,
  }),
});

export const DummyCardMapper = new Mapper({
  from: ({ dummyCardId, ...dummyCard }) =>
    Map({
      id: dummyCardId,
      ...dummyCard,
    }),
  to: ({ id, slotNo, cardContainerId, name, ...dummyCard }) => ({
    employeeId: id,
    dummyCardId: id,
    ...dummyCard,
  }),
});

export const CardMoveMapper = new Mapper({
  to: ({
    targetCardContainerId,
    deploymentDetails,
    projects,
    ...cardMove
  }) => ({
    targetCardContainerId,
    DeploymentDetail: deploymentDetails
      ? DeploymentDetailMapper.to({
          ...deploymentDetails,
          cardContainerId: targetCardContainerId,
        })
      : deploymentDetails,
    projects: List(projects).map(ProjectMapper.from),
    ...cardMove,
  }),
});

export const FacilityMapper = new Mapper({
  initial: () =>
    Map({
      id: undefined,
      name: undefined,
      field: FieldMapper.initial(),
    }),
  from: ({ facilityId, mobileFacilityId, modified, ...facility }) => {
    const type = facilityId ? FACILITY_TYPE.FIXED : FACILITY_TYPE.MOBILE;

    return Map({
      type,
      facility: FacilityMapper.initial(),
      id: type === FACILITY_TYPE.FIXED ? facilityId : mobileFacilityId,
      modified: moment(modified),
      ...facility,
    });
  },
});

export const FieldMapper = new Mapper({
  initial: () =>
    Map({
      id: undefined,
      name: undefined,
    }),
  from: ({ fieldId, modified, ...field }) =>
    Map({
      id: fieldId,
      modified: moment(modified),
      ...field,
    }),
});

export const DeploymentDetailMapper = new Mapper({
  from: ({ modified, deploymentStart, start, end, ...deploymentDetail }) =>
    Map({
      name: deploymentDetail.groupShortName || deploymentDetail.groupName,
      startDate: start ? moment(start) : start,
      deploymentStartDate: deploymentStart
        ? moment(deploymentStart)
        : deploymentStart,
      endDate: end ? moment(end) : end,
      modified: moment(modified),
      type: CALENDAR_DATE_RANGE_TYPE.DEPLOYMENT,
      ...deploymentDetail,
    }),
  to: ({ departure, arrival, from, ...deploymentDetail }) => ({
    start: from
      ? dateFromDateHourMinute(from.date, from.hour, from.minute).toISOString()
      : arrival
      ? dateFromDateHourMinute(
          arrival.date,
          arrival.hour,
          arrival.minute,
        ).toISOString()
      : undefined,
    end:
      departure &&
      dateFromDateHourMinute(
        departure.date,
        departure.hour,
        departure.minute,
      ).toISOString(),
    ...deploymentDetail,
  }),
});

export const QualificationMapper = new Mapper({
  initial: () => EMPTY_MAP,
  from: ({ qualificationId, modified, ...qualification }) =>
    Map({
      id: qualificationId,
      modified: moment(modified),
      ...qualification,
    }),
});

export const CrewMapper = new Mapper({
  from: ({ crewId, modified, ...crew }) =>
    Map({
      id: crewId,
      modified: moment(modified),
      ...crew,
    }),
});

export const CountryMapper = new Mapper({
  from: ({ countryId, twoLetterCode, ...country }) =>
    Map({
      id: countryId,
      code: twoLetterCode,
      ...country,
    }),
});

export const CompanyMapper = new Mapper({
  from: ({ companyId, modified, ...company }) =>
    Map({
      id: companyId,
      modified: moment(modified),
      ...company,
    }),
  to: ({ id, ...company }) => ({
    companyId: id,
    ...company,
  }),
});

export const SearchHitMapper = new Mapper({
  from: searchHit => Map(searchHit),
});

export const SearchQueryMapper = new Mapper({
  initial: () =>
    Map({
      term: "",
    }),
  from: searchQuery => Map(searchQuery),
  to: searchQuery => searchQuery,
});

export const SearchResultMapper = new Mapper({
  initial: () =>
    Map({
      hitCount: 0,
      hits: List(),
      query: SearchQueryMapper.initial(),
    }),
  from: ({ query, hits, ...searchResult }) =>
    Map({
      query: SearchQueryMapper.from(query),
      hits: List(hits).map(SearchHitMapper.from),
      ...searchResult,
    }),
});

export const WorkScheduleMapper = new Mapper({
  from: ({ endDate, modified, startDate, workScheduleId, ...workSchedule }) =>
    Map({
      id: workScheduleId,
      endDate: endDate ? moment(endDate) : endDate,
      modified: moment(modified),
      startDate: startDate ? moment(startDate) : startDate,
      ...workSchedule,
    }),
  to: ({ id, endDate, startDate, ...workSchedule }) => ({
    workScheduleId: id,
    endDate: endDate ? endDate.format(START_END_DATE_FORMAT) : null,
    startDate: startDate ? startDate.format(START_END_DATE_FORMAT) : null,
    ...workSchedule,
  }),
});

export const WorkScheduleDefinitionMapper = new Mapper({
  initial: () =>
    Map({
      cycle: "",
      dayCount: 0,
    }),
  from: ({
    workScheduleDefinitionId,
    cycleDayCount,
    shiftCycle,
    ...workScheduleDefinition
  }) =>
    Map({
      cycle: shiftCycle,
      dayCount: cycleDayCount,
      id: workScheduleDefinitionId,
      ...workScheduleDefinition,
    }),
});

export const workScheduleOverrideReasonToString = memoize(
  reason =>
    ({
      [undefined]: "",
      [WORK_SCHEDULE_OVERRIDE_REASONS.AVAILABLE]: "Available",
      [WORK_SCHEDULE_OVERRIDE_REASONS.SICK]: "Sick",
      [WORK_SCHEDULE_OVERRIDE_REASONS.TRAINING]: "Training",
      [WORK_SCHEDULE_OVERRIDE_REASONS.MILITARY_SERVICE]: "Military service",
      [WORK_SCHEDULE_OVERRIDE_REASONS.LEAVE_OF_ABSCENCE]: "Leave of abscence",
      [WORK_SCHEDULE_OVERRIDE_REASONS.PROJECT_BASE]: "Project base",
      [WORK_SCHEDULE_OVERRIDE_REASONS.VACATION]: "Vacation",
      [WORK_SCHEDULE_OVERRIDE_REASONS.LABOR_UNION]: "Labor union",
      [WORK_SCHEDULE_OVERRIDE_REASONS.QUARANTINE]: "Quarantine",
      [WORK_SCHEDULE_OVERRIDE_REASONS.LAYOFF]: "Layoff",
    }[reason]),
);

export const workScheduleTypeToString = memoize(
  type =>
    ({
      [undefined]: "",
      [WORK_SCHEDULE_STATUS.OFF]: "Off",
      [WORK_SCHEDULE_STATUS.ON]: "On",
      [WORK_SCHEDULE_STATUS.OFF_FLEX]: "Off flex",
    }[type]),
);

export const WorkScheduleOverrideMapper = new Mapper({
  from: ({
    workScheduleOverrideId,
    startDate,
    endDate,
    modified,
    ...workScheduleOverride
  }) =>
    Map({
      id: workScheduleOverrideId,
      startDate: startDate ? moment(startDate) : startDate,
      endDate: endDate ? moment(endDate) : endDate,
      modified: moment(modified),
      type: CALENDAR_DATE_RANGE_TYPE.AVAILABILITY_OVERRIDE,
      ...workScheduleOverride,
    }),
  to: ({ startDate, endDate, ...workScheduleOverride }) => ({
    startDate: startDate ? startDate.format(START_END_DATE_FORMAT) : null,
    endDate: endDate ? endDate.format(START_END_DATE_FORMAT) : null,
    ...workScheduleOverride,
  }),
});

export const CompanyRelationMapper = new Mapper({
  from: ({ toDate, fromDate, companyGroupRelationId, ...companyRelation }) =>
    Map({
      id: companyGroupRelationId,
      toDate: toDate ? moment(toDate) : toDate,
      fromDate: fromDate ? moment(fromDate) : fromDate,
      ...companyRelation,
    }),
});

export const EmployeeGroupMapper = new Mapper({
  from: ({ employeeGroupId, modified, ...employeeGroup }) =>
    Map({
      id: employeeGroupId,
      modified: moment(modified),
      ...employeeGroup,
    }),
  to: ({ id, ...employeeGroup }) => ({
    employeeGroupId: id,
    ...employeeGroup,
  }),
});

export const EmployeeCourseFileMapper = new Mapper({
  from: ({ originalFileName, ...employeeCourseFile }) => ({
    ...employeeCourseFile,
    name: originalFileName,
  }),
});

export const CourseMapper = new Mapper({
  from: ({ courseId, courseType, endDate, expirationDate, files, ...course }) =>
    Map({
      id: courseId,
      endDate: endDate ? moment(endDate) : endDate,
      expirationDate: expirationDate ? moment(expirationDate) : expirationDate,
      files: Set(files)
        .map(EmployeeCourseFileMapper.from)
        .sortBy(file => file.modified),
      ...course,
    }),
  to: ({ id }) => ({
    courseId: id,
  }),
});

export const WorkScheduleQueryMapper = new Mapper({
  to: ({ startDate, endDate }) => ({
    startDate: startDate ? startDate.format(START_END_DATE_FORMAT) : startDate,
    endDate: endDate ? endDate.format(START_END_DATE_FORMAT) : endDate,
  }),
});

export const WorkDayMapper = new Mapper({
  initial: () => EMPTY_MAP,
  from: ({ date, ...workDay }) =>
    Map({
      date: date ? moment(date) : undefined,
      ...workDay,
    }),
});

export const CardCompetencyStatusMapper = new Mapper({
  from: cardCompetencyStatus =>
    Map(cardCompetencyStatus).set("uniqueId", createGuid()),
});

export const DashboardStatsMapper = new Mapper({
  from: ({
    employeesCountByCardContainerType,
    subContractorsCountByCardContainerType,
    personnelOffshoreByCrew,
    ...otherStats
  }) =>
    Map({
      employeesCountByCardContainerType: Map(employeesCountByCardContainerType),
      subContractorsCountByCardContainerType: Map(
        subContractorsCountByCardContainerType,
      ),
      personnelOffshoreByCrew: Map(personnelOffshoreByCrew)
        .update("series", series =>
          series.map(({ key, data }) => ({
            data,
            name: employeeTypeToString(key),
          })),
        )
        .toJS(),
      ...otherStats,
    }),
});

export const WorkScheduleTimeSeriesMapper = new Mapper({
  from: ({ series, categories }) =>
    Map({
      series: series.map(({ key, data }) => {
        const { background } = theme.mixins.workScheduleOverride(key);

        return {
          color: background,
          data: data.map(value => (value === 0 ? null : value)),
          name: workScheduleOverrideReasonToString(key),
        };
      }),
      categories: categories.map(timestamp =>
        moment(timestamp).format(START_END_DATE_FORMAT),
      ),
    }),
});

export const ProjectMapper = new Mapper({
  from: ({ projectId, startTime, endTime, ...project }) =>
    Map({
      projectId: projectId,
      startTime: startTime ? moment(startTime) : startTime,
      endTime: endTime ? moment(endTime) : endTime,
      ...project,
    }),
});

export const requirementTypeToString = {
  [REQUIREMENT_TYPES.COURSE]: "Course",
  [REQUIREMENT_TYPES.SKILL]: "Skill",
};

export const criticalityToString = {
  [CRITICALITY.CRITICAL]: "Critical",
  [CRITICALITY.NOT_CRITICAL]: "Not Critical",
};
